import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import heroImage1 from "@/assets/hero-1.jpg";
import heroImage2 from "@/assets/hero-2.jpg";
import heroImage1Webp from "@/assets/hero-1.webp";
import heroImage2Webp from "@/assets/hero-2.webp";
import heroImage1WebpTablet from "@/assets/hero-1-tablet.webp";
import heroImage2WebpTablet from "@/assets/hero-2-tablet.webp";
import heroImage1WebpMobile from "@/assets/hero-1-mobile.webp";
import heroImage2WebpMobile from "@/assets/hero-2-mobile.webp";

const slides = [
  {
    image: heroImage1,
    imageWebp: heroImage1Webp,
    imageWebpTablet: heroImage1WebpTablet,
    imageWebpMobile: heroImage1WebpMobile,
    subtitle: "Architecture & Interior Design",
    title: "Crafting Immersive Experiences",
  },
  {
    image: heroImage2,
    imageWebp: heroImage2Webp,
    imageWebpTablet: heroImage2WebpTablet,
    imageWebpMobile: heroImage2WebpMobile,
    subtitle: "Dubai · Krakow",
    title: "Redefining Functional Elegance",
  },
];

const heroSrcSet = (s: typeof slides[0]) =>
  `${s.imageWebpMobile} 768w, ${s.imageWebpTablet} 1280w, ${s.imageWebp} 1920w`;
const heroSizes = "100vw";

const HeroSection = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden" aria-label="Featured projects">
      {/* Preload LCP hero image with responsive srcset so the browser fetches the right size early */}
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={slides[0].imageWebp}
          // @ts-expect-error - imagesrcset/imagesizes are valid HTML but missing in React types
          imagesrcset={heroSrcSet(slides[0])}
          imagesizes={heroSizes}
          fetchpriority="high"
          type="image/webp"
        />
      </Helmet>
      {slides.map((slide, i) => (
        <motion.div
          key={i}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{
            opacity: current === i ? 1 : 0,
            scale: current === i ? 1 : 1.1,
          }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
          aria-hidden={current !== i}
        >
          <picture>
            <source
              srcSet={heroSrcSet(slide)}
              sizes={heroSizes}
              type="image/webp"
            />
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
              width={1920}
              height={1080}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "low"}
              decoding="async"
            />
          </picture>
          <div className="absolute inset-0 bg-foreground/60" aria-hidden="true" />
        </motion.div>
      ))}

      <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-24 md:pb-32">
        <motion.div
          key={current}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <p className="text-base md:text-lg font-body font-light tracking-[0.3em] uppercase text-primary-foreground/90 mb-5" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
            {slides[current].subtitle}
          </p>
          <h1 className="font-heading text-6xl md:text-8xl lg:text-9xl tracking-wider uppercase leading-none text-primary-foreground max-w-5xl" style={{ textShadow: "0 4px 20px rgba(0,0,0,0.5)" }}>
            {slides[current].title}
          </h1>
        </motion.div>

        <div className="flex gap-3 mt-10" role="tablist" aria-label="Slide navigation">
          {slides.map((slide, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              role="tab"
              aria-selected={current === i}
              aria-label={`Show slide ${i + 1}: ${slide.title}`}
              className={`h-px transition-all duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                current === i ? "w-16 bg-primary-foreground" : "w-8 bg-primary-foreground/40"
              }`}
            />
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        aria-hidden="true"
      >
        <div className="w-px h-12 bg-primary-foreground/40 animate-pulse" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
